import { nameof } from "ts-simple-nameof";
import { useEffect, useRef } from "react";
import { useRefillCardPage } from "./hooks/useRefillCardPage";
import { IRefillCardFormModel } from "./models/refillCardFormModel";
import styles from "./RefillCard.module.scss";
import { FormProvider } from "react-hook-form";
import { HiddenField } from "../../../../common/components/forms/inputs/hiddenField/HiddenField";
import { DefaultFormSelectField } from "../../../../common/components/forms/inputs/dropdown/DefaultFormSelectField";
import { RenderWhen } from "../../../../common/infrastructure/components/RenderWhen";
import {
  CurrencyType,
  OnboardingItemKey,
  PlanConsumerType,
} from "../../../../common/enums";
import { CurrencyTypeSymbols } from "../../../../common/labels";
import { DefaultDateField } from "../../../../common/components/forms/inputs/dateField/DefaultDateField";
import { DefaultInputField } from "../../../../common/components/forms/inputs/InputField/DefaultInputField";
import { DefaultButton } from "../../../../common/components/button/defaultButton/DefaultButton";
import { routePaths } from "../../../../common/infrastructure/routes/routePaths";
import { SingleSectionPageLayout } from "../../../../common/components/common/SingleSectionPageLayout";
import { PageMode } from "../../../../common/types";
import { useOnboarding } from "../../../onboarding/OnboardingProvider";

interface IRefillCardProps {
  pageMode: PageMode;
}

// Track across mounts so the empty flow only auto-fires once per session
let hasAutoActivatedEmptyFlow = false;

export const RefillCard = ({ pageMode }: IRefillCardProps) => {
  const {
    form,
    availablePlans,
    emptyMessage,
    paymentOptions,
    currentlySelectedPlan,
    availableUsers,
    availableFamilies,
    isEdit,
    isLoading,
    studioInstructors,
    initialData,
    pricePerVisit,
    removePlan,
    onSave,
    onSaveAndOpenCashdesk,
  } = useRefillCardPage(pageMode);

  const onboarding = useOnboarding();
  const hasAutoActivatedRef = useRef(false);

  const isActive = onboarding?.isActive ?? false;
  const pendingItemKeys = onboarding?.pendingItemKeys;
  const setHelpStepOverride = onboarding?.setHelpStepOverride;
  const activateStep = onboarding?.activateStep;

  // Always set help override so "?" button works, even when isActive is false
  useEffect(() => {
    if (isLoading) return;
    const isEmpty = !!emptyMessage;
    setHelpStepOverride?.(
      isEmpty
        ? OnboardingItemKey.RefillCardEmpty
        : OnboardingItemKey.RefillCardGuide,
    );
  }, [emptyMessage, isLoading, setHelpStepOverride]);

  // Auto-activate only when onboarding is active
  useEffect(() => {
    if (!isActive || isLoading) return;

    const isEmpty = !!emptyMessage;

    if (isEmpty) {
      if (!hasAutoActivatedEmptyFlow && !hasAutoActivatedRef.current) {
        hasAutoActivatedRef.current = true;
        hasAutoActivatedEmptyFlow = true;
        activateStep?.(OnboardingItemKey.RefillCardEmpty);
      }
    } else {
      if (
        !hasAutoActivatedRef.current &&
        pendingItemKeys?.has(OnboardingItemKey.RefillCardGuide)
      ) {
        hasAutoActivatedRef.current = true;
        activateStep?.(OnboardingItemKey.RefillCardGuide);
      }
    }
  }, [emptyMessage, isLoading, isActive, pendingItemKeys, activateStep]);

  // Clear help override on unmount
  useEffect(() => {
    return () => setHelpStepOverride?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // New refills are always recorded in EUR, but editing a legacy card must keep showing
  // (and saving) the BGN it was actually sold in — the field is read-only either way.
  const selectedCurrencyId = form.watch("currencyId");
  const displayedCurrency =
    selectedCurrencyId === CurrencyType.BGN ? CurrencyType.BGN : CurrencyType.EUR;
  const currencySymbol = CurrencyTypeSymbols[displayedCurrency];

  const pageTitle = isEdit
    ? "Редактиране на абонаментна карта"
    : "Презареждане на карта";

  const planTooltip = isEdit
    ? "Това поле не може да бъде редактирано."
    : undefined;

  const userIdTooltip = isEdit
    ? "Това поле не може да бъде редактирано."
    : "Всички потребители, които нямат асоцииран групов план и не са изтрити";

  const individualUserIdTooltip = isEdit
    ? "Това поле не може да бъде редактирано."
    : "Всички потребители, които нямат асоцииран индивидуален план и не са изтрити";

  const familyIdTooltip = isEdit
    ? "Това поле не може да бъде редактирано."
    : "Всички семейства, които нямат асоцииран семеен групов план и не са изтрити";

  const numberOfVisitsLabel = isEdit ? "Оставащи посещения." : "Брой посещения";

  const submitButtonLabel = isEdit ? "Запази" : "Зареди";
  const deleteButtonLabel = initialData?.currentlySelectedSubscription
    ?.isDeleted
    ? "Възстанови плана"
    : "Изтрий плана";

  const children = (
    <>
      <div className={styles.root_fields}>
        <HiddenField
          name={nameof<IRefillCardFormModel>((x) => x.editUserPlanId)}
        />
        <div className={styles.root_section}>
          <div className={styles.root_section_inner_container}>
            <DefaultFormSelectField
              name={nameof<IRefillCardFormModel>((x) => x.planId)}
              options={availablePlans}
              label="План"
              tooltip={planTooltip}
              required
              disabled={!!initialData?.currentlySelectedSubscription}
            />
          </div>
        </div>
        <div className={styles.root_section}>
          <div className={styles.root_section_inner_container}>
            <RenderWhen
              condition={
                currentlySelectedPlan?.consumerType === PlanConsumerType.User
              }
            >
              <DefaultFormSelectField
                name={nameof<IRefillCardFormModel>((x) => x.userId)}
                options={availableUsers}
                label="Потребител"
                tooltip={userIdTooltip}
                required
                disabled={!!initialData?.currentlySelectedSubscription}
              />
            </RenderWhen>
            <RenderWhen
              condition={
                currentlySelectedPlan?.consumerType === PlanConsumerType.Family
              }
            >
              <DefaultFormSelectField
                name={nameof<IRefillCardFormModel>((x) => x.familyId)}
                options={availableFamilies}
                label="Семейство"
                tooltip={familyIdTooltip}
                required
                disabled={!!initialData?.currentlySelectedSubscription}
              />
            </RenderWhen>
          </div>
        </div>

        <div className={styles.root_section}>
          <div className={styles.root_section_inner_container}>
            <DefaultFormSelectField
              name={nameof<IRefillCardFormModel>((x) => x.paymentTypeId)}
              options={paymentOptions}
              label="Начин на плащане"
              includeId={false}
              required
            />
          </div>
        </div>

        <div className={styles.root_section}>
          <div className={styles.root_section_inner_container}>
            <DefaultDateField
              name={nameof<IRefillCardFormModel>((x) => x.startDate)}
              label="Дата на започване на плана"
              placeholder=""
            />
          </div>
        </div>

        <div className={styles.root_section}>
          <div className={styles.root_section_inner_container}>
            <DefaultDateField
              name={nameof<IRefillCardFormModel>((x) => x.endDate)}
              label="Последен активен ден на плана"
              placeholder=""
            />
          </div>
        </div>

        <div className={styles.root_section}>
          <div className={styles.root_section_inner_container}></div>
          <DefaultInputField
            name={nameof<IRefillCardFormModel>((x) => x.numberOfVisits)}
            placeholder={"20"}
            label={numberOfVisitsLabel}
            type="number"
            required
            min={isEdit ? 0 : 1}
          />
        </div>

        <div className={styles.root_section}>
          <div className={styles.root_section_inner_container}></div>
          <DefaultInputField
            name={nameof<IRefillCardFormModel>((x) => x.price)}
            currencyName={nameof<IRefillCardFormModel>((x) => x.currencyId)}
            placeholder={"20"}
            label={"Цена"}
            type="number"
            step={0.01}
            required
            min={1}
            currency={displayedCurrency}
          />
        </div>

        <div className={styles.root_section} data-onboarding="sold-by-field">
          <DefaultFormSelectField
            label="Продадена от"
            name={nameof<IRefillCardFormModel>((x) => x.createdById)}
            options={studioInstructors}
            tooltip="Служителят, който ще продава картата. Важно е, за да може да се генерират правилни репорти."
            required
          />
        </div>
      </div>

      <RenderWhen condition={!!pricePerVisit}>
        <div className={styles.root_section}>
          <div className={styles.root_price_per_visit}>
            Цена на посещение:{" "}
            <strong>
              {pricePerVisit} {currencySymbol}
            </strong>
          </div>
        </div>
      </RenderWhen>

      <div className={styles.root_buttons}>
        <DefaultButton label={submitButtonLabel} type="submit" />
        <RenderWhen condition={!isEdit}>
          <DefaultButton
            label={"Зареди и отвори каса"}
            design="outline"
            type="button"
            onClick={onSaveAndOpenCashdesk}
          />
        </RenderWhen>
        <RenderWhen condition={!!isEdit}>
          <DefaultButton
            label={deleteButtonLabel}
            onClick={removePlan}
            design="outline"
          />
        </RenderWhen>
        <div className={styles.root_section_footer_text}>
          {/* Ако клиентът има добавен имейл, ще получи на него информация за новия
          си план. */}
        </div>
      </div>
    </>
  );

  const emptyMessageElement = (
    <div className={styles.root_empty_wrapper}>
      <div className={styles.root_empty_wrapper_message}>{emptyMessage}</div>

      <div className={styles.root_empty_wrapper_buttons}>
        <DefaultButton label="Към 'Планове'" href={routePaths.plans} />
        <DefaultButton label="Към 'Клиенти'" href={routePaths.users} />
      </div>
    </div>
  );

  return (
    <>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSave)}>
          <SingleSectionPageLayout
            title={pageTitle}
            maxHeight={"90%"}
            children={emptyMessage ? emptyMessageElement : children}
          />
        </form>
      </FormProvider>
    </>
  );
};
